import React, { useEffect, useState } from 'react';
import { Form, Input, Layout, message, Skeleton, Select } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from 'lodash';

import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';

import BannerService from 'services/banner';

import 'react-quill-new/dist/quill.snow.css';
import './style.scss';

const { Content } = Layout;
const { Option } = Select;

const FIELDS = {
  DESKTOP_MEDIA_URL: 'image_url',
  LINK_URL: 'link_url',
  MOBILE_MEDIA_URL: 'mobile_image',
  TITLE: 'title',
};

export function BannerCreateEdit() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [formLayout] = useState('vertical');
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [value, setValue] = useState({});
  const [selectedProtocol, setSelectedProtocol] = useState('https://');
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const selectBefore = (
    <Select
      value={selectedProtocol}
      style={{ width: 90 }}
      onChange={setSelectedProtocol}>
      <Option value="http://">Http://</Option>
      <Option value="https://">Https://</Option>
    </Select>
  );

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  async function getBanner() {
    if (id) {
      setLoadingPage(true);
      const {
        data: { data },
      } = await BannerService.getBanner(id);
      const protocol = data.link_url.includes('https') ? 'https://' : 'http://';
      const link_url = data.link_url.replace(protocol, '');
      const bannerDetail = { ...data, link_url };
      setValue(bannerDetail);
      setLoadingPage(false);
      setSelectedProtocol(protocol);
    }
  }

  async function handleSubmit(values) {
    try {
      setLoadingButton(true);
      const link_url = `${selectedProtocol}${values.link_url}`;

      if (id) {
        values.weight = Number(values.weight) * 1000;
        await BannerService.updateBannerDetail([
          {
            ...value,
            ...values,
            link_url,
          },
        ]);
        message.success('Successfully updated banner');
      } else {
        await BannerService.createBanner({ ...value, ...values, link_url });
        message.success('Successfully created banner');
      }
      navigate('/banners');
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(key, e) {
    if (e) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function handleChangeValue(url, key) {
    setValue((prevState) => ({
      ...prevState,
      [key]: url,
    }));
  }

  useEffect(() => {
    getBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Layout className="pd-cms-banners main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">{id ? 'Edit' : 'Create New'} Banner</div>
          <Content className="pageContent">
            {loadingPage ? (
              <Skeleton active loading={loadingPage}></Skeleton>
            ) : (
              <Form
                form={form}
                layout={formLayout}
                onFinish={handleSubmit}
                initialValues={value}>
                <div className="section-container">
                  <div className="inner-section">
                    <Form.Item
                      label="Title"
                      name={FIELDS.TITLE}
                      rules={[
                        { message: 'Please input Title!', required: true },
                      ]}
                      {...formItemLayout}>
                      <Input
                        placeholder="Write banner name here"
                        onChange={changeValue.bind(this, FIELDS.TITLE)}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Banner Url"
                      name={FIELDS.LINK_URL}
                      rules={[
                        { message: 'Please input link url!', required: true },
                      ]}
                      {...formItemLayout}>
                      <Input
                        addonBefore={selectBefore}
                        placeholder="Write banner url here"
                        onChange={changeValue.bind(this, FIELDS.LINK_URL)}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Desktop Image (1280x400 px)"
                      {...formItemLayout}>
                      <PdUploader
                        afterUpload={handleChangeValue}
                        imgClassName="w-full"
                        imgUrl={value[FIELDS.DESKTOP_MEDIA_URL]}
                        keyValue={FIELDS.DESKTOP_MEDIA_URL}
                        name="Desktop Image (1280x400 px)"
                      />
                    </Form.Item>
                    <Form.Item
                      label="Mobile Image (640x320 px)"
                      {...formItemLayout}>
                      <PdUploader
                        afterUpload={handleChangeValue}
                        imgClassName="w-70"
                        imgUrl={value[FIELDS.MOBILE_MEDIA_URL]}
                        keyValue={FIELDS.MOBILE_MEDIA_URL}
                        name="Mobile Image (640x320 px)"
                      />
                    </Form.Item>
                  </div>
                  <Form.Item {...buttonItemLayout}>
                    <div className="buttonSection mt-40">
                      <PdButton
                        full
                        htmlType="submit"
                        loading={loadingButton}
                        type="primary">
                        Publish
                      </PdButton>
                    </div>
                  </Form.Item>
                </div>
              </Form>
            )}
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default BannerCreateEdit;
